import React, { useState, useCallback, Fragment } from 'react'
import { Navigate, useParams } from 'react-router-dom'
import { Box, useTheme } from '@mui/material'
import { useMutation, useQuery } from '@apollo/client'
import moment from 'moment'
import * as XLSX from 'xlsx'
import Chart from 'react-apexcharts'
import _sortBy from 'lodash/sortBy'
import _get from 'lodash/get'
import _groupBy from 'lodash/groupBy'
import JSZip from 'jszip'
import { saveAs } from 'file-saver'
import Promise from 'bluebird'

import Loading from '../../components/Loading'
import participantReportQuery from '../../graphql/query/participantReport'
import downloadFaceMutation from '../../graphql/mutation/downloadFace'
import activitiesCountQuery from '../../graphql/query/activitiesCount'
import activitiesTicketTypeQuery from '../../graphql/query/activitiesTicketTypeCount'
import activitiesQuery from '../../graphql/type-composers/activity'
import eventByIdQuery from '../../graphql/query/eventById'
import { useToast } from '../../contexts/ToastContext'
import {
  StatsHeader,
  StatsMetricsCards,
  StatsCharts,
  StatsShirtDistribution,
  StatsTicketTypes,
  StatsExportDialog,
} from '../../components/stats'

const StatsPage = () => {
  const theme = useTheme()
  const { showSuccess, showError } = useToast()
  const [exporting, setExporting] = useState(false)
  const [getLoading, setLoading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [selectedCheckinChartDataDate, setSelectedCheckinChartDataDate] = useState('')
  const [timePeriod, setTimePeriod] = useState('Monthly')
  const { eventId } = useParams()
  const { data, loading } = useQuery(eventByIdQuery, {
    variables: { _id: eventId },
  })
  const activitiesCount = useQuery(activitiesCountQuery, {
    variables: { eventId, interval: 20 },
    pollInterval: 20000,
    fetchPolicy: 'network-only',
  })
  const activitiesTicketType = useQuery(activitiesTicketTypeQuery, {
    variables: { eventId },
    pollInterval: 12000,
    fetchPolicy: 'network-only',
  })
  const activities = useQuery(activitiesQuery, {
    variables: { eventId },
    pollInterval: 7000,
    fetchPolicy: 'network-only',
  })
  const participantReport = useQuery(participantReportQuery, {
    fetchPolicy: 'network-only',
    skip: true,
  })
  const [downloadFace] = useMutation(downloadFaceMutation)
  const colorList = ['#E5BC30', '#E96583', '#00B2B2']

  const handleChangeCheckInDate = (e) => {
    setSelectedCheckinChartDataDate(e.target.value)
  }

  const handleExport = useCallback(async () => {
    try {
      setExporting(true)
      const participantReportResult = await participantReport.refetch({
        eventId,
      })
      const workbook = XLSX.utils.book_new()
      const worksheet = XLSX.utils.json_to_sheet(
        participantReportResult.data.participantReport.map(
          ({ event: { name: eventName }, options, ...participant }) => ({
            ...participant,
            checkin_date: participant.timestamp
              ? moment(participant.timestamp).format('DD/MM/YYYY')
              : '',
            eventName,
            ...options,
          })
        )
      )
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Participants')
      XLSX.writeFile(workbook, `participants-${moment().format('YYYYMMDDHHmmSS')}.xlsx`)
      showSuccess(
        `Exported ${participantReportResult.data.participantReport.length} participant(s) successfully.`,
        'Export Successful'
      )
    } catch (err) {
      console.error('Export error:', err)
      showError('Failed to export participants. Please try again.', 'Export Failed')
    } finally {
      setExporting(false)
    }
  }, [setExporting, eventId, participantReport, showSuccess, showError])

  const saveZip = async (data, pageCount, PAGESIZE) => {
    const zip = new JSZip()
    let img = zip.folder('ExportFace')
    const batches = await Promise.each([...new Array(pageCount)], async (_, page) => {
      console.log(page, 'page')
      if (page !== 0) {
        data = data.slice(PAGESIZE)
      }
      const dataRow = data.slice(0, PAGESIZE)
      const promises = await dataRow.map((url, index) =>
        fetch(`${url.photo}`).then((r) =>
          r.blob().then((e) => {
            img.file(`${url.bib}.jpeg`, e)
          })
        )
      )
      const code = await Promise.all(promises)
      if (code) {
        return code
      }
    })

    if (batches) {
      await zip.generateAsync({ type: 'blob' }).then(function (content) {
        saveAs(content, `ExportFaceAll.zip`)
      })
    }
  }

  const handleFaceExport = useCallback(async () => {
    try {
      setLoading(true)
      const downloadFaceExportResult = await downloadFace({
        variables: { eventId },
      })
      const total = downloadFaceExportResult.data.downloadFace.length
      const PAGESIZE = 20
      let PAGECOUNT = Math.ceil(total / PAGESIZE)
      let halfCount = 0
      if (total > 4000) {
        halfCount = Math.ceil(total / 2)
      }
      let data = downloadFaceExportResult.data.downloadFace
      let data1 = []
      let data2 = []
      let pageCount1 = 0
      let pageCount2 = 0
      if (halfCount !== 0) {
        data1 = downloadFaceExportResult.data.downloadFace.slice(0, halfCount)
        data2 = downloadFaceExportResult.data.downloadFace.slice(halfCount, total)
        pageCount1 = data1.length
        pageCount2 = data2.length
        await saveZip(data1, pageCount1, PAGESIZE)
        await saveZip(data2, pageCount2, PAGESIZE)
      } else {
        await saveZip(data, PAGECOUNT, PAGESIZE)
      }
      showSuccess(`Exported ${total} face image(s) successfully.`, 'Face Export Successful')
      setProgress(100)
    } catch (err) {
      console.error('Face export error:', err)
      showError('Failed to export face images. Please try again.', 'Face Export Failed')
      setProgress(0)
    } finally {
      setLoading(false)
    }
  }, [setLoading, eventId, downloadFace, showSuccess, showError])

  const formateTimeTicket = (time) => {
    const timex = moment(time).format('DD/MM/YYYY hh:mm:ss')
    return timex
  }

  let checkinChartDataDaily = {
    options: {},
    series: [],
  }
  const daily = _get(activitiesCount, 'data.activitiesCountByTime')
  const ticketTypes = _get(activitiesTicketType, 'data.activitiesCountByTicketType')

  let activitiesDateList = []
  if (daily && daily.length > 0) {
    activitiesDateList = [
      ...new Set(
        daily
          .map((x) => {
            if (x.time == null) return null
            return x.time.split('T')[0]
          })
          .filter(Boolean)
      ),
    ]
  }
  const getDailyList = (selectedDate) => {
    const selectDateList = daily.filter(
      (ea) => ea.time != null && ea.time.split('T')[0] === selectedDate
    )
    const selectDx = selectDateList.map((sea) => ({
      time: new Date(sea.time).getHours(),
      count: sea.count,
    }))
    const checkin24hrs = Array.from({ length: 24 }, (v, i) => i).map((ea) => {
      const countx = selectDx.filter((ef) => ef.time === ea)
      return countx.length > 0 ? countx[0].count : 0
    })
    return checkin24hrs
  }
  if (
    selectedCheckinChartDataDate === '' &&
    daily &&
    daily.length > 0 &&
    activitiesDateList.length > 0
  ) {
    setSelectedCheckinChartDataDate(activitiesDateList[0])
  }

  // Calculate metrics
  const allCheckin =
    activitiesCount.data &&
    activitiesCount.data.activitiesCount
      .filter((activity) => !!activity.lastActivity)
      .reduce((prev, cur) => prev + cur.count, 0)
  const totalParticipants =
    activitiesCount.data &&
    activitiesCount.data.activitiesCount.reduce((prev, cur) => prev + cur.count, 0)
  const remainParticipants =
    activitiesCount.data &&
    activitiesCount.data.activitiesCount
      .filter((activity) => !activity.lastActivity)
      .reduce((prev, cur) => prev + cur.count, 0)
  const verifiedPercentage =
    totalParticipants > 0 ? ((allCheckin / totalParticipants) * 100).toFixed(1) : 0
  const shirtsDistributed = allCheckin // Assuming shirts distributed equals verified count

  // Verification status data
  const normalVerified = allCheckin || 0
  const approvedByStaff = 0 // This would need to come from actual data
  const notYetVerified = remainParticipants || 0
  const totalForVerification = totalParticipants || 1
  const normalPercentage = ((normalVerified / totalForVerification) * 100).toFixed(1)
  const approvedPercentage = ((approvedByStaff / totalForVerification) * 100).toFixed(1)
  const notVerifiedPercentage = ((notYetVerified / totalForVerification) * 100).toFixed(1)

  // Shirt distribution data
  let shirts = []
  const activitiesCountByShirt = _get(activitiesCount, 'data.activitiesCountByShirt')
  if (activitiesCountByShirt) {
    const shirtTypeGroup = _groupBy(activitiesCountByShirt, 'shirtType')
    const keys = Object.keys(shirtTypeGroup)
    shirts = keys.map((key) => {
      const shirtsx = _sortBy(shirtTypeGroup[key], 'shirtSize')
      return {
        shirtType: key,
        sizes: shirtsx.map((s) => ({
          size: s.shirtSize,
          distributed: s.count,
          total: s.stock || s.count, // Use stock if available, otherwise use count
        })),
      }
    })
  }

  checkinChartDataDaily = {
    options: {
      chart: {
        id: 'bar',
        toolbar: {
          show: false,
        },
        background: 'transparent',
      },
      dataLabels: {
        enabled: false,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 4,
          columnWidth: '60%',
          distributed: false,
        },
      },
      xaxis: {
        categories: [
          0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23,
        ],
        title: { text: 'Time (Hr)' },
        labels: {
          style: {
            fontSize: '12px',
            colors: theme.palette.mode === 'light' ? '#666' : '#999',
          },
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      yaxis: {
        min: 0,
        max: selectedCheckinChartDataDate
          ? Math.max(...getDailyList(selectedCheckinChartDataDate), 1)
          : 1,
        labels: {
          style: {
            fontSize: '14px',
            colors: theme.palette.mode === 'light' ? '#666' : '#999',
          },
          formatter(val) {
            return val.toFixed(0)
          },
        },
      },
      legend: {
        show: false,
      },
      grid: {
        borderColor:
          theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.08)' : 'rgba(255, 255, 255, 0.08)',
        strokeDashArray: 3,
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      colors: ['#1976d2'],
      tooltip: {
        theme: theme.palette.mode === 'light' ? 'light' : 'dark',
      },
    },
    series: [
      {
        name: 'Participants',
        data: selectedCheckinChartDataDate
          ? getDailyList(selectedCheckinChartDataDate)
          : Array(24).fill(0),
      },
    ],
  }

  // Verification Status Donut Chart
  const verificationChartData = {
    options: {
      chart: {
        type: 'donut',
        background: 'transparent',
      },
      labels: ['Normal', 'Approved by staff', 'Not yet verified'],
      colors: ['#9C27B0', '#BA68C8', '#BDBDBD'],
      dataLabels: {
        enabled: true,
        formatter: function (val, opts) {
          return opts.w.globals.series[opts.seriesIndex].toFixed(1) + '%'
        },
        style: {
          fontSize: '12px',
          fontWeight: 600,
        },
      },
      plotOptions: {
        pie: {
          donut: {
            size: '70%',
            labels: {
              show: false,
            },
          },
        },
      },
      legend: {
        show: false,
      },
      tooltip: {
        theme: theme.palette.mode === 'light' ? 'light' : 'dark',
        y: {
          formatter: function (val) {
            return val.toFixed(1) + '%'
          },
        },
      },
    },
    series: [
      parseFloat(normalPercentage),
      parseFloat(approvedPercentage),
      parseFloat(notVerifiedPercentage),
    ],
  }

  const getColorStatus = (status) => {
    let color1
    switch (status) {
      case 'checkin':
        color1 = {
          color: '#E5BC3D',
          bg: '#FCF7E7',
        }
        break
      case 'pickup':
        color1 = {
          color: '#00B2B2',
          bg: '#E0F6F6',
        }
        break
      default:
        color1 = {
          color: '#000',
          bg: '#000',
        }
    }
    return color1
  }

  if (activitiesCount.loading || loading) {
    return <Loading />
  }

  if (!eventId) {
    return <Navigate to="/events" replace />
  }

  const currentDateTime = moment().format('DD MMM YYYY, HH:mm') + ' hrs.'

  return (
    <Fragment>
      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        {/* Dashboard Header */}
        <StatsHeader
          currentDateTime={currentDateTime}
          activitiesCount={activitiesCount}
          data={data}
          getLoading={getLoading}
          exporting={exporting}
          onFaceExport={handleFaceExport}
          onExport={handleExport}
        />

        {/* Key Metrics Cards */}
        <StatsMetricsCards
          totalParticipants={totalParticipants}
          allCheckin={allCheckin}
          verifiedPercentage={verifiedPercentage}
          shirtsDistributed={shirtsDistributed}
        />

        {/* Charts Row */}
        <StatsCharts
          selectedCheckinChartDataDate={selectedCheckinChartDataDate}
          timePeriod={timePeriod}
          activitiesDateList={activitiesDateList}
          checkinChartDataDaily={checkinChartDataDaily}
          verificationChartData={verificationChartData}
          normalVerified={normalVerified}
          normalPercentage={normalPercentage}
          approvedByStaff={approvedByStaff}
          approvedPercentage={approvedPercentage}
          notYetVerified={notYetVerified}
          notVerifiedPercentage={notVerifiedPercentage}
          onCheckInDateChange={(e) => handleChangeCheckInDate(e)}
          onTimePeriodChange={(e) => setTimePeriod(e.target.value)}
        />

        {/* Ticket Type Counts */}
        <StatsTicketTypes ticketTypes={ticketTypes} />

        {/* Shirt Distribution Cards */}
        <StatsShirtDistribution shirts={shirts} />
      </Box>

      {/* Export Dialog */}
      <StatsExportDialog open={getLoading} progress={progress} onClose={() => setLoading(false)} />
    </Fragment>
  )
}

export default StatsPage
